import React, { useState } from 'react';
import { demoSectionCardStackStyle, DEMO_MINI_CARD_GRID_GAP_PX, FERTILITY_DEMO_SECTION_BORDER } from '../designSystem';

const FONT = '"Plus Jakarta Sans", -apple-system, sans-serif';

export const appointments = [
  {
    id: 'apt-001',
    type: 'Monitoring scan',
    date: '2026-04-07',
    time: '8:00 AM',
    clinicLine: 'Ohio Fertility Network',
    location: 'Westerville',
    sub: 'follicle & estrogen check (stim day 8)',
    notes: '',
    status: 'upcoming',
    dayLabel: 'Tomorrow',
    stimDay: 8,
    isExpanded: true,
  },
  {
    id: 'apt-002',
    type: 'Pre-retrieval check',
    date: '2026-04-14',
    time: '9:00am',
    clinicLine: 'Pinnacle Fertility',
    doctor: 'Dr. Jain',
    sub: null,
    notes: '',
    status: 'upcoming',
    dayLabel: 'In 7 days',
    isExpanded: false,
  },
];

export const todaysLabs = [
  { marker: 'Estradiol (E2)', value: '1,842', unit: 'pg/mL' },
  { marker: 'LH', value: '1.2', unit: 'mIU/mL' },
  { marker: 'Progesterone', value: '0.4', unit: 'ng/mL' },
];

const FOLLICLE_BUCKET_LABELS = ['≥18mm', '14–17mm', '10–13mm', '<10mm'];

const initialFollicleBuckets = {
  right: [0, 2, 3, 0],
  left: [0, 1, 4, 2],
};

export const questionsSeed = [
  { id: 'q1', text: 'What does my follicle count mean for retrieval timing?', checked: true, source: null },
  { id: 'q2', text: 'My estrogen felt lower than cycle 1 on day 7 — is that expected?', checked: false, source: 'cycle1' },
  { id: 'q3', text: 'How close are my lead follicles to trigger size (18–20mm)?', checked: false, source: null },
  { id: 'q4', text: 'With AFC of 12, should I watch for OHSS signs?', checked: false, source: 'afc' },
];

export const pastVisits = [
  {
    date: '2026-03-18',
    name: 'Baseline scan · Cycle 2 start',
    sub: 'Pinnacle Fertility · E2 38 · AFC 12 · Gonal-F started',
    badgeEmoji: '🙂',
    labs: [
      { label: 'Estradiol', value: '38', unit: 'pg/mL' },
      { label: 'FSH', value: '7.2', unit: 'mIU/mL' },
      { label: 'LH', value: '4.1', unit: 'mIU/mL' },
      { label: 'AFC', value: '12', unit: 'follicles' },
    ],
    outcomesLeft: [
      { k: 'Protocol', v: 'Antagonist' },
      { k: 'Started', v: 'Gonal-F 225 IU' },
    ],
    outcomesRight: [
      { k: 'Start date', v: 'Mar 27, 2026' },
      { k: 'Next scan', v: 'Apr 1 · stim day 5' },
    ],
    savedNote:
      'Dr. Jain said my AFC looks good — 12 follicles is a strong start. Starting Gonal-F 225 IU tonight. Baseline E2 of 38 is right where they want it. Feeling nervous but ready.',
  },
  {
    date: '2026-03-10',
    name: 'Nurse teach · injection training',
    sub: 'Gonal-F, Menopur, Ganirelix',
    badgeEmoji: null,
    labs: null,
    singleOutcome: 'Medications reviewed — Gonal-F, Menopur, Ganirelix',
    savedNote: 'Injection training done. Nurse was so patient. Nervous about starting tomorrow but feel prepared.',
  },
];

const clabel = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--muted)',
  marginBottom: 0,
};

const sectionCardWrap = {
  background: 'var(--white)',
  border: FERTILITY_DEMO_SECTION_BORDER,
  borderRadius: 14,
  padding: '16px 18px',
};

const saveBtn = {
  background: 'var(--pink, #d4537e)',
  color: 'white',
  fontSize: 11,
  fontWeight: 600,
  padding: '6px 16px',
  borderRadius: 8,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

/** Same yellow note surface as My Doctors → private notes */
const PRIVATE_NOTE_BG = '#FFFDE7';
const PRIVATE_NOTE_BORDER = '#F9E57D';
const PRIVATE_NOTE_TEXT = '#4A3F00';

function dateBoxFromIso(iso) {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return { month: '—', day: '—' };
  return {
    month: d.toLocaleDateString('en-US', { month: 'short' }),
    day: String(d.getDate()),
  };
}

function formatPastDate(iso) {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return { month: '—', day: '—' };
  return {
    month: d.toLocaleDateString('en-US', { month: 'short' }),
    day: String(d.getDate()),
  };
}

/** Emoji + label — matches “How are you feeling?” scale (negative → positive). */
const FEELING_OPTIONS = [
  { emoji: '😩', label: 'Rough' },
  { emoji: '😢', label: 'Sad' },
  { emoji: '😞', label: 'Low' },
  { emoji: '😐', label: 'Okay' },
  { emoji: '😑', label: 'Meh' },
  { emoji: '🙂', label: 'Good' },
  { emoji: '😊', label: 'Great' },
  { emoji: '😄', label: 'Happy' },
  { emoji: '🤩', label: 'Amazing' },
];

/**
 * /demo/fertility/appointment-prep — shell hero + Nova bar from ChapterDemo + pageHeaderConfig.
 */
export default function FertilityDemoAppointmentPrep() {
  const [expandedById, setExpandedById] = useState(() =>
    appointments.reduce((acc, a) => {
      acc[a.id] = Boolean(a.isExpanded);
      return acc;
    }, {})
  );
  const [questions, setQuestions] = useState(() => questionsSeed.map((q) => ({ ...q })));
  const [postVisitNotes, setPostVisitNotes] = useState('');
  const [emotionalRating, setEmotionalRating] = useState('🙂');
  const [newQuestion, setNewQuestion] = useState('');
  const [openPastDate, setOpenPastDate] = useState('2026-03-18');
  const [bloodLabs, setBloodLabs] = useState(() => todaysLabs.map((l) => ({ ...l })));
  const [follicleBuckets, setFollicleBuckets] = useState(() => ({
    right: [...initialFollicleBuckets.right],
    left: [...initialFollicleBuckets.left],
  }));

  const toggleExpanded = (id) => {
    setExpandedById((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleQuestion = (id) => {
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, checked: !q.checked } : q)));
  };

  const addOwnQuestion = () => {
    const t = newQuestion.trim();
    if (!t) return;
    setQuestions((prev) => [...prev, { id: `q_${Date.now()}`, text: t, checked: false, source: null }]);
    setNewQuestion('');
  };

  const togglePastVisit = (date) => {
    setOpenPastDate((prev) => (prev === date ? null : date));
  };

  const updateBloodLab = (idx, value) => {
    setBloodLabs((prev) => prev.map((l, i) => (i === idx ? { ...l, value } : l)));
  };

  const setFollicleBucket = (ovary, idx, raw) => {
    const n = raw === '' ? 0 : Math.max(0, parseInt(raw, 10) || 0);
    setFollicleBuckets((prev) => ({
      ...prev,
      [ovary]: prev[ovary].map((v, i) => (i === idx ? n : v)),
    }));
  };

  return (
    <div
      style={{
        ...demoSectionCardStackStyle,
        fontFamily: FONT,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <section>
        <div style={sectionCardWrap}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div>
              <h2 className="section-title">Upcoming appointments</h2>
              <p className="section-descriptor" style={{ marginTop: 3 }}>
                Tap any visit to open your prep kit.
              </p>
            </div>
            <button
              type="button"
              style={{
                fontSize: 12,
                color: 'var(--pink)',
                fontWeight: 600,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                padding: 0,
                flexShrink: 0,
              }}
            >
              + Add appointment
            </button>
          </div>

          {appointments.map((apt) => {
            const box = dateBoxFromIso(apt.date);
            const isExp = expandedById[apt.id];

            if (apt.id === 'apt-001') {
              return (
                <div key={apt.id} style={{ marginBottom: 8 }}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleExpanded(apt.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleExpanded(apt.id);
                      }
                    }}
                    style={{
                      background: 'rgba(212, 83, 126, 0.04)',
                      borderBottom: '0.5px solid rgba(212, 83, 126, 0.15)',
                      borderRadius: isExp ? '10px 10px 0 0' : 10,
                      padding: '13px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      cursor: 'pointer',
                      width: '100%',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ textAlign: 'center', flexShrink: 0, width: 44 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: 'var(--pink-d, #993556)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                        }}
                      >
                        {box.month}
                      </div>
                      <div
                        style={{
                          fontSize: 22,
                          fontWeight: 700,
                          color: 'var(--text)',
                          fontFamily: 'Georgia, serif',
                          lineHeight: 1,
                        }}
                      >
                        {box.day}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: 'var(--pink-d)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          marginBottom: 2,
                        }}
                      >
                        {apt.type}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                        Tomorrow · {apt.time} · {apt.clinicLine}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                        {apt.location} · {apt.sub}
                      </div>
                      <span
                        style={{
                          background: 'var(--amber-l, #fef0eb)',
                          color: 'var(--amber, #ad3f1a)',
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 10,
                          marginTop: 4,
                          display: 'inline-flex',
                        }}
                      >
                        {apt.dayLabel}
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--pink)', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {isExp ? '▾ Prep kit open' : '▶ Open prep kit'}
                    </span>
                  </div>

                  {isExp ? (
                    <div style={{ marginTop: 22 }}>
                      {/* Section 1 — Questions + notes (side by side) */}
                      <div
                        style={{
                          padding: '12px 16px',
                          borderBottom: '0.5px solid rgba(212, 83, 126, 0.1)',
                          background: 'linear-gradient(140deg, rgba(251,234,240,0.65) 0%, rgba(225,245,238,0.65) 100%)',
                          border: '0.5px solid rgba(212, 83, 126, 0.16)',
                          borderRadius: 10,
                        }}
                      >
                        <div
                          style={{
                            padding: 0,
                            display: 'grid',
                            gridTemplateColumns: 'minmax(0, 40%) minmax(0, 60%)',
                            gap: 14,
                            alignItems: 'stretch',
                          }}
                        >
                          <div
                            style={{
                              minWidth: 0,
                              background: 'var(--white, #fff)',
                              border: '0.5px solid var(--border, #e8e0d8)',
                              borderRadius: 8,
                              padding: '10px 12px',
                            }}
                          >
                            <div style={{ marginBottom: 8 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Questions for Dr. Jain</span>
                            </div>
                            {questions.map((q, qi) => (
                              <div
                                key={q.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 9,
                                  padding: '7px 0',
                                  borderBottom: qi < questions.length - 1 ? '0.5px solid var(--sand-2, #ede8df)' : 'none',
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => toggleQuestion(q.id)}
                                  aria-pressed={q.checked}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: 3,
                                    border: q.checked ? '1.5px solid var(--pink)' : '1.5px solid var(--border, #e8e0d8)',
                                    flexShrink: 0,
                                    marginTop: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    background: q.checked ? 'var(--pink)' : 'var(--white)',
                                    color: q.checked ? 'white' : 'transparent',
                                    fontSize: 10,
                                    fontWeight: 700,
                                    padding: 0,
                                    fontFamily: 'inherit',
                                  }}
                                >
                                  {q.checked ? '✓' : ''}
                                </button>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: q.checked ? 'var(--muted)' : 'var(--text)',
                                      lineHeight: 1.45,
                                      textDecoration: q.checked ? 'line-through' : 'none',
                                    }}
                                  >
                                    {q.text}
                                  </div>
                                </div>
                              </div>
                            ))}
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                marginTop: 8,
                              }}
                            >
                              <input
                                value={newQuestion}
                                onChange={(e) => setNewQuestion(e.target.value)}
                                placeholder="Add your own question..."
                                style={{
                                  flex: 1,
                                  border: '0.5px solid var(--border)',
                                  borderRadius: 8,
                                  padding: '6px 10px',
                                  fontSize: 11,
                                  fontFamily: 'inherit',
                                }}
                              />
                              <button
                                type="button"
                                onClick={addOwnQuestion}
                                style={{
                                  background: 'var(--pink)',
                                  color: 'white',
                                  fontSize: 10,
                                  fontWeight: 600,
                                  padding: '6px 12px',
                                  borderRadius: 8,
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                  flexShrink: 0,
                                }}
                              >
                                + Add
                              </button>
                            </div>
                          </div>

                          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ marginBottom: 8 }}>
                              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>My notes</span>
                            </div>
                            <div
                              style={{
                                background: 'rgba(255,255,255,0.72)',
                                border: '0.5px solid rgba(212, 83, 126, 0.2)',
                                borderRadius: 8,
                                padding: '10px 12px',
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                              }}
                            >
                              <textarea
                                value={postVisitNotes}
                                onChange={(e) => setPostVisitNotes(e.target.value)}
                                placeholder="What did Dr. Jain say? Follicle sizes, E2 result, any changes to the plan, next steps..."
                                style={{
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  border: `0.5px solid ${PRIVATE_NOTE_BORDER}`,
                                  borderRadius: 6,
                                  padding: '8px 10px',
                                  fontSize: 12,
                                  fontFamily: 'inherit',
                                  color: PRIVATE_NOTE_TEXT,
                                  lineHeight: 1.5,
                                  minHeight: 132,
                                  resize: 'none',
                                  background: PRIVATE_NOTE_BG,
                                  flex: 1,
                                }}
                              />
                              <div style={{ marginTop: 8, width: '100%', minWidth: 0 }}>
                                <span
                                  style={{
                                    display: 'block',
                                    fontSize: 10,
                                    color: 'var(--pink-d)',
                                    fontWeight: 600,
                                    marginBottom: 8,
                                  }}
                                >
                                  How do you feel?
                                </span>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'space-between',
                                    gap: 10,
                                    width: '100%',
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    role="radiogroup"
                                    aria-label="How do you feel?"
                                    style={{
                                      flex: 1,
                                      minWidth: 0,
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(9, minmax(0, 1fr))',
                                      gap: 4,
                                      alignItems: 'start',
                                    }}
                                  >
                                    {FEELING_OPTIONS.map(({ emoji, label }) => {
                                      const selected = emotionalRating === emoji;
                                      return (
                                        <button
                                          key={emoji}
                                          type="button"
                                          onClick={() => setEmotionalRating(emoji)}
                                          aria-pressed={selected}
                                          aria-label={`${label}, ${emoji}`}
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 2,
                                            cursor: 'pointer',
                                            background: selected ? 'rgba(212, 83, 126, 0.08)' : 'transparent',
                                            border: selected ? '2px solid var(--pink, #d4537e)' : '2px solid transparent',
                                            borderRadius: 10,
                                            padding: '5px 2px 4px',
                                            minWidth: 0,
                                            width: '100%',
                                            lineHeight: 1.15,
                                            fontFamily: 'inherit',
                                            boxSizing: 'border-box',
                                          }}
                                        >
                                          <span style={{ fontSize: 16, lineHeight: 1 }}>{emoji}</span>
                                          <span
                                            style={{
                                              fontSize: 9,
                                              fontWeight: selected ? 700 : 500,
                                              color: selected ? 'var(--pink-d, #993556)' : 'var(--muted)',
                                              letterSpacing: selected ? '0.01em' : 0,
                                              textAlign: 'center',
                                              whiteSpace: 'normal',
                                              overflow: 'visible',
                                              lineHeight: 1.15,
                                              maxWidth: '100%',
                                            }}
                                          >
                                            {label}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                  <button type="button" style={{ ...saveBtn, flexShrink: 0, alignSelf: 'flex-end' }}>
                                    Save notes
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Section 2 — Today's labs (full width; same horizontal inset as prep kit above) */}
                      <div
                        style={{
                          padding: '12px 16px',
                          borderBottom: 'none',
                          borderRadius: '0 0 10px 10px',
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <span style={clabel}>Today&apos;s labs</span>
                        </div>

                        <div style={{ width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              minWidth: 0,
                              width: '100%',
                              border: '0.5px solid rgba(212, 83, 126, 0.28)',
                              background: 'rgba(251, 234, 240, 0.18)',
                              borderRadius: 8,
                              padding: '10px 12px',
                              boxSizing: 'border-box',
                            }}
                          >
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                              {bloodLabs.map((lab, li) => {
                                const filled = String(lab.value ?? '').trim() !== '';
                                return (
                                  <div
                                    key={lab.marker}
                                    style={{
                                      background: 'var(--sand, #f9f6f1)',
                                      borderRadius: 8,
                                      padding: '7px 10px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: 'var(--muted)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        marginBottom: 3,
                                      }}
                                    >
                                      {lab.marker}
                                    </div>
                                    <input
                                      value={lab.value}
                                      onChange={(e) => updateBloodLab(li, e.target.value)}
                                      placeholder="—"
                                      style={{
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        padding: '3px 6px',
                                        textAlign: 'right',
                                        fontFamily: 'inherit',
                                        ...(filled
                                          ? {
                                              background: 'var(--sage-l, #e1f5ee)',
                                              border: '0.5px solid rgba(29, 158, 117, 0.25)',
                                              color: '#085041',
                                              fontSize: 13,
                                              fontWeight: 700,
                                              fontStyle: 'normal',
                                              borderRadius: 5,
                                            }
                                          : {
                                              background: 'var(--white, #fffcf9)',
                                              border: '1.5px dashed var(--border, #e8e0d8)',
                                              color: 'var(--muted, #7a6b63)',
                                              fontSize: 11,
                                              fontWeight: 400,
                                              fontStyle: 'italic',
                                              borderRadius: 5,
                                            }),
                                      }}
                                    />
                                    <div style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'right', marginTop: 2 }}>{lab.unit}</div>
                                  </div>
                                );
                              })}
                            </div>

                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: 'var(--muted)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                marginBottom: 5,
                              }}
                            >
                              Follicles (ultrasound)
                            </div>

                            {['right', 'left'].map((ovaryKey) => {
                              const label = ovaryKey === 'right' ? 'Right ovary' : 'Left ovary';
                              const buckets = follicleBuckets[ovaryKey];
                              const total = buckets.reduce((a, b) => a + b, 0);
                              return (
                                <div
                                  key={ovaryKey}
                                  style={{
                                    background: 'var(--sand, #f9f6f1)',
                                    borderRadius: 8,
                                    padding: '8px 10px',
                                    marginBottom: 5,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        color: 'var(--muted)',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                      }}
                                    >
                                      {label}
                                    </span>
                                    {total > 0 ? (
                                      <span
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          background: 'var(--sage-l)',
                                          color: '#085041',
                                          padding: '1px 6px',
                                          borderRadius: 5,
                                        }}
                                      >
                                        {total}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4 }}>
                                    {FOLLICLE_BUCKET_LABELS.map((bl, bi) => {
                                      const n = buckets[bi];
                                      const filled = n > 0;
                                      return (
                                        <div
                                          key={bl}
                                          style={{
                                            background: 'var(--white)',
                                            borderRadius: 6,
                                            padding: '4px 6px',
                                            textAlign: 'center',
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: 10,
                                              color: 'var(--muted)',
                                              fontWeight: 600,
                                              display: 'block',
                                              marginBottom: 2,
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {bl}
                                          </span>
                                          <input
                                            type="number"
                                            min={0}
                                            value={n === 0 ? '' : n}
                                            onChange={(e) => setFollicleBucket(ovaryKey, bi, e.target.value)}
                                            placeholder="—"
                                            style={{
                                              width: '100%',
                                              textAlign: 'center',
                                              fontSize: 13,
                                              fontWeight: 700,
                                              border: 'none',
                                              padding: '1px 0',
                                              fontFamily: 'inherit',
                                              boxSizing: 'border-box',
                                              ...(filled
                                                ? {
                                                    background: 'var(--sage-l, #e1f5ee)',
                                                    color: '#085041',
                                                    borderRadius: 4,
                                                  }
                                                : {
                                                    background: 'transparent',
                                                    color: 'var(--muted)',
                                                    borderBottom: '1.5px dashed var(--border, #e8e0d8)',
                                                    borderRadius: 0,
                                                  }),
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                              <span style={{ fontSize: 10, color: 'var(--muted)' }}>Update after your visit</span>
                              <button type="button" style={saveBtn}>
                                Save labs
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            }

            const b2 = dateBoxFromIso(apt.date);
            const open2 = expandedById[apt.id];
            return (
              <div key={apt.id} style={{ marginBottom: 8 }}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleExpanded(apt.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpanded(apt.id);
                    }
                  }}
                  style={{
                    background: 'rgba(212, 83, 126, 0.04)',
                    borderBottom: '0.5px solid rgba(212, 83, 126, 0.15)',
                    borderRadius: open2 ? '10px 10px 0 0' : 10,
                    padding: '13px 16px',
                    marginBottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    width: '100%',
                  }}
                >
                  <div style={{ textAlign: 'center', flexShrink: 0, width: 44 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--pink-d, #993556)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                      }}
                    >
                      {b2.month}
                    </div>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: 'var(--text)',
                        fontFamily: 'Georgia, serif',
                        lineHeight: 1,
                      }}
                    >
                      {b2.day}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--pink-d)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        marginBottom: 2,
                      }}
                    >
                      {apt.type}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {apt.time} · {apt.clinicLine}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      {apt.doctor}
                    </div>
                    <span
                      style={{
                        background: 'var(--amber-l, #fef0eb)',
                        color: 'var(--amber, #ad3f1a)',
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 10,
                        marginTop: 4,
                        display: 'inline-flex',
                      }}
                    >
                      {apt.dayLabel}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--pink)', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {open2 ? '▾ Prep kit open' : '▶ Open prep kit'}
                  </span>
                </div>
                {open2 ? (
                  <div
                    style={{
                      border: '0.5px solid rgba(212, 83, 126, 0.16)',
                      borderTop: 'none',
                      borderRadius: '0 0 10px 10px',
                      padding: '12px 16px',
                      marginBottom: 0,
                      background: 'linear-gradient(140deg, rgba(251,234,240,0.65) 0%, rgba(225,245,238,0.65) 100%)',
                      fontSize: 12,
                      color: 'var(--text)',
                      lineHeight: 1.5,
                      boxSizing: 'border-box',
                    }}
                  >
                    Pre-retrieval checklist and questions for Dr. Jain will appear here as your visit approaches.
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <div style={sectionCardWrap}>
          <h2 className="section-title">Past visits</h2>
          <p className="section-descriptor" style={{ marginTop: 3, marginBottom: 12 }}>
            Notes and outcomes from previous appointments.
          </p>
          {pastVisits.map((v) => {
            const db = formatPastDate(v.date);
            const isOpen = openPastDate === v.date;
            return (
              <div
                key={v.date}
                style={{
                  border: '0.5px solid var(--border)',
                  borderRadius: 10,
                  marginBottom: 8,
                  overflow: 'hidden',
                  opacity: 0.85,
                }}
              >
                <button
                  type="button"
                  onClick={() => togglePastVisit(v.date)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    background: 'transparent',
                    border: 'none',
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ textAlign: 'center', flexShrink: 0, width: 44 }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--pink-d, #993556)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                      }}
                    >
                      {db.month}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', fontFamily: 'Georgia, serif', lineHeight: 1 }}>{db.day}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{v.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{v.sub}</div>
                    <span
                      style={{
                        background: 'var(--sage-l, #e1f5ee)',
                        color: '#085041',
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 10,
                        marginTop: 4,
                        display: 'inline-flex',
                      }}
                    >
                      {v.badgeEmoji ? `Notes saved ${v.badgeEmoji}` : 'Notes saved'}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--pink)', fontWeight: 600, flexShrink: 0 }}>
                    {isOpen ? '▾ Collapse' : '▶ Expand'}
                  </span>
                </button>
                {isOpen ? (
                  <div style={{ borderTop: '0.5px solid var(--sand-2, #ede8df)', padding: '12px 14px' }}>
                    {v.labs ? (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: DEMO_MINI_CARD_GRID_GAP_PX, marginBottom: 10 }}>
                          {v.labs.map((lab) => (
                            <div
                              key={lab.label}
                              style={{
                                background: 'var(--sand, #f9f6f1)',
                                borderRadius: 7,
                                padding: '6px 8px',
                                textAlign: 'center',
                              }}
                            >
                              <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 2 }}>{lab.label}</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{lab.value}</div>
                              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{lab.unit}</div>
                            </div>
                          ))}
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 0,
                            marginBottom: 8,
                            width: '100%',
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            {v.outcomesLeft.map((row, i) => (
                              <div
                                key={row.k}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '4px 0',
                                  borderBottom: i < v.outcomesLeft.length - 1 ? '0.5px solid var(--sand-2, #ede8df)' : 'none',
                                  fontSize: 11,
                                }}
                              >
                                <span style={{ color: 'var(--muted)' }}>{row.k}</span>
                                <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.v}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            {v.outcomesRight.map((row, i) => (
                              <div
                                key={row.k}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '4px 0',
                                  borderBottom: i < v.outcomesRight.length - 1 ? '0.5px solid var(--sand-2, #ede8df)' : 'none',
                                  fontSize: 11,
                                }}
                              >
                                <span style={{ color: 'var(--muted)' }}>{row.k}</span>
                                <span style={{ fontWeight: 600, color: 'var(--text)', textAlign: 'right' }}>{row.v}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 11, color: 'var(--text)', marginBottom: 8, padding: '4px 0' }}>{v.singleOutcome}</div>
                    )}
                    <div
                      style={{
                        background: '#fdf2f6',
                        border: '0.5px solid rgba(212,83,126,0.15)',
                        borderRadius: 7,
                        padding: '8px 10px',
                        fontSize: 11,
                        fontStyle: 'italic',
                        color: 'var(--pink-d, #993556)',
                        lineHeight: 1.5,
                      }}
                    >
                      {v.savedNote}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
